/**
 * event-page — the event page IS the shop: one scrolling surface where the
 * buyer selects, enters contact, and pays. Composes <event-header>
 * (identity), <type-selector section="wholeEvent"> (the tickets tied to no
 * single day, above the chips and absent where there are none),
 * <programme-day-selector> (the sticky day chips, on an event
 * whose types declare their own entry windows and nothing at all on one that
 * does not), <type-selector> (the selected day's own rows, the basket empty
 * on land), the name+email
 * contact fields (locale rides silently, browser-detected), and
 * a payment section carrying the REAL <schmancy-revolute> widget. The
 * contact fields and the payment section belong to the buyer's first pick:
 * they appear the moment the basket holds a line — a free ticket among
 * them, a real selection at a zero total — and stay mounted through every
 * beat past shopping, so a live card iframe survives and the refusal frame
 * has its section to render in. The rows
 * carry name, price, and quantity; the total rides the pay area. "paid" is
 * the confirming beat continued: the same wait stands until the mounted
 * `.sync` pushes the buyer to the real /order ticket surface.
 *
 * Pure view — zero I/O. It renders checkout.state.ts and
 * event-page.state.ts and mutates them on gestures; the mounted app's own
 * `.sync` drives the payment session (order create, amount-sync, the
 * confirming → paid advance) against that app's backing. `phaseOverride`
 * lets a studio force any journey phase without a live session; production
 * leaves it null and the phase derives from the session + journey.
 *
 * The payment stack stays mounted through submission and server fulfillment.
 * The originating Card Pay button keeps `.busy` across both spans; header,
 * ticket picks and contact fields stay inert until the terminal answer. The
 * SDK iframe remains interactive for a genuine 3DS challenge. A cancelled
 * challenge returns the journey to shopping. Initial preparation uses the
 * stack's skeleton; a user-fired preparation retry retains its clicked control.
 * A wallet leg has no control of ours to carry the wait — the SDK's express
 * button is an opaque iframe — so a full-viewport processing overlay carries
 * it there, mounted only for that span and transparent to the pointer. Every
 * leg narrates the beat through one sr-only live region.
 * The refusal frame renders once a checkout is refused post-authorisation
 * (checkout.refusedAt): its body and its one restart action switch on the
 * server's own refusalReason — sold-out capacity, the basket having moved
 * before payment went through (nothing charged, continue at the updated
 * total), or an opaque server-side refusal.
 */
import { area } from "@mhmo91/schmancy";
import {
	curtain,
	fullHeight,
	gravity,
	overflowWithin,
	reveal,
} from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-busy> and <schmancy-spinner>.
import "@mhmo91/schmancy/busy";
// Side-effect: registers <sch-notification>.
import { $notify } from "@mhmo91/schmancy/notification";
import { organizationPathPrefix } from "../../workspace/organization.state.ts";
// Side-effect: registers <schmancy-revolute>.
import "@mhmo91/schmancy/payment";
import type {
	SchmancyRevolute,
	SchmancyRevoluteErrorDetail,
	SchmancyRevoluteNoticeCopy,
	SchmancyRevoluteTrackingDetail,
} from "@mhmo91/schmancy/payment";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { formatCurrency } from "@mhmo91/rx";
import { Order } from "@momo/types";
import { html } from "lit";
import { property, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { distinctUntilChanged, map, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { legalContentState, legalRetry$ } from "../../legal/legal.state.ts";
import { salesPaused } from "../../licence/licence.state.ts";
import "../checkout-disclosures.view.ts";
import "../voucher/voucher-code.view.ts";
import {
	amountInSync,
	amountSyncing,
	type CheckoutPhase,
	checkoutJourney,
	checkoutRetry$,
	checkoutTestMode,
	existingTicketToken,
	freeConfirm$,
	freeConfirmable,
	paymentMode,
	paymentPublicKey,
	paymentSession,
	paymentSessionSpent$,
	paymentStackBlock,
	paymentStackInert,
	releasesOnProcessingStop,
	resetPaymentSession,
	sessionEnvUsable,
} from "../checkout.state.ts";
import { appliedVoucher } from "../voucher/code.state.ts";
import "../event-header/event-header.view.ts";
import { eventDetailState } from "../event-header/event-header.state.ts";
import { playSoundCue } from "../sound-cues.ts";
import {
	addressRequired,
	basketFilled,
	basketProgrammeDays,
	type ContactDraft,
	contactState,
	contactValid,
	nothingToBuy,
	orderLines,
	orderTotal,
	programmeDays,
	refundEntryEventId,
	selectedProgrammeDay,
	selectedProgrammeDayState,
	setContact,
	totalBasketQty,
	wholeEventRows,
} from "./event-page.state.ts";
import "../programme-day-selector.view.ts";
import "../type-selector.view.ts";

// The Kleinbetragsrechnung ceiling reads as the round figure the law names —
// "250 €", not "250,00 €" — so the line explaining the address field sounds
// like a rule rather than a second running total beside the pay button's.
const ceilingFormatter = new Intl.NumberFormat("de-DE", {
	style: "currency",
	currency: "EUR",
	maximumFractionDigits: 0,
});

// ISO 3166-1 alpha-2 values; labels follow the buyer's UI language.
const BILLING_COUNTRY_CODES =
	"AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(
		" ",
	);

@schmancyElement("event-page")
export class EventPageView extends LocalizeMixin(SchmancyElement) {
	/** A forced journey phase for studio review; null derives from the live
	 *  session + journey. */
	@property({ attribute: false }) phaseOverride: CheckoutPhase | null = null;
	@state() private billingCountry = "";
	@state() private billingPostcode = "";

	/** True once @schmancy-revolute-success has fired for the CURRENT
	 *  submission attempt — reset at every processing:true (a fresh attempt
	 *  starting). Read on processing:false so a dismissed 3DS challenge (or
	 *  any other conclusion the widget reaches with no success) can release
	 *  checkoutJourney back to "shopping" without undoing a genuine
	 *  success: the widget's own processing:false always precedes its own
	 *  success dispatch, in the same synchronous callback, and
	 *  confirmPayment() re-confirms "confirming" immediately after — before
	 *  any render observes the in-between value. */
	private widgetSucceeded = false;

	/** True between the widget's processing:true and its processing:false.
	 *  A widget also reports processing:false as its initial value on connect,
	 *  so one mounting under a journey a reload resumed would otherwise release
	 *  a payment that is still in flight. */
	private widgetProcessing = false;

	/** Sticky jump bar renders only while the payment section is
	 *  off-screen. */
	@state() private paymentInView = false;
	@state() private retryingPayment = false;
	/** A card submit is carrying the wait on its own button. A press alone
	 *  does not set it: an incomplete form's press submits nothing. */
	@state() private cardSettlement = false;

	private checkoutRootRef = createRef<HTMLDivElement>();
	private paymentWidgetRef = createRef<SchmancyRevolute>();
	private contactFieldsRef = createRef<HTMLDivElement>();
	/** The main ticket list — where the entry overlay's own "go to the tickets"
	 *  request lands, since a code that reveals a hidden type puts a row in it
	 *  the buyer has not seen and would otherwise have to hunt for. */
	private typeSelectorRef = createRef<HTMLElement>();

	private paymentSectionObserver: IntersectionObserver | undefined;
	private observedPaymentSection: Element | undefined;

	override connectedCallback() {
		super.connectedCallback();
		paymentSession.$.pipe(
			map((current) => current.status),
			distinctUntilChanged(),
			takeUntil(this.disconnecting),
		).subscribe((status) => {
			if (status !== "creating") this.retryingPayment = false;
		});
	}

	override disconnectedCallback() {
		this.paymentSectionObserver?.disconnect();
		// Cleared so a reconnect re-observes (updated()'s same-element guard).
		this.observedPaymentSection = undefined;
		super.disconnectedCallback();
	}

	override updated() {
		// The widget's contract: boot as soon as the parent confirms the
		// payment section is visible with a non-empty cart. Every payment
		// method (card, Apple/Google Pay) stays visible; the request-order
		// handler hands the widget the live session token on demand. The
		// widget's own guard makes this call safe to repeat on every render.
		const revolute = this.paymentWidgetRef.value;
		if (revolute && this.paymentInView && orderTotal.get() > 0) {
			revolute.initializePaymentMethodsIfNeeded();
		}

		const section =
			this.renderRoot.querySelector("#payment-section") ?? undefined;
		if (section === this.observedPaymentSection) return;
		this.paymentSectionObserver?.disconnect();
		this.observedPaymentSection = section;
		if (!section) {
			this.paymentInView = false;
			return;
		}
		this.paymentSectionObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry) this.paymentInView = entry.isIntersecting;
			},
			{ threshold: 0.25 },
		);
		this.paymentSectionObserver.observe(section);
	}

	/** The journey phase the page renders — the override wins; otherwise it
	 *  derives from the live session + the post-confirm journey. */
	private phase(): CheckoutPhase {
		if (this.phaseOverride) return this.phaseOverride;
		const journey = checkoutJourney.value;
		if (journey === "confirming") return "confirming";
		if (journey === "paid") return "paid";
		const session = paymentSession.value;
		if (session.status === "error" && session.reason === "refused")
			return "refused";
		// A zero-total basket carries no payment leg. Nor is a session from a
		// retired environment mountable — checkout.sync is already restarting
		// it, so the page holds its preparing skeleton rather than showing an
		// error the buyer cannot act on.
		return (session.status === "ready" && sessionEnvUsable.get()) ||
			orderTotal.get() === 0
			? "ready"
			: "preparing";
	}

	/** The widget dispatches this whenever card OR wallet needs an order
	 *  token; the host answers with the live checkout session's token. */
	private requestOrder(detail: {
		resolve: (o: { token: string }) => void;
		reject: (err: Error) => void;
	}) {
		const current = paymentSession.value;
		if (current.status !== "ready") {
			detail.reject(
				new Error(this.t("storefront.eventPage.payArea.noPaymentSession")),
			);
			return;
		}
		detail.resolve({ token: current.token });
	}

	/** The widget confirmed — the mounted `.sync` advances confirming → paid
	 *  from its backing's own signal. A zero-total basket mounts no widget and
	 *  has nothing to authorise, so the same gesture on its own commit control
	 *  IS the authorisation: freeConfirm$ asks the mounted `.sync` to retire
	 *  the open document and create the free one, and the confirming beat
	 *  covers that write exactly as it covers a card's. */
	private confirmPayment() {
		checkoutJourney.replace("confirming");
		if (orderTotal.get() === 0) freeConfirm$.next();
	}

	/** Refusal retry — no payment settled, so the buyer's basket is
	 *  preserved. resetPaymentSession() alone leaves the session idle with
	 *  no re-land in flight (the mounted `.sync`'s auto-land already fired
	 *  once on page-land and won't re-fire), so checkoutRetry$ re-triggers
	 *  the same land the transient-error Retry button uses, against the
	 *  buyer's current (corrected) basket. */
	private restart() {
		checkoutJourney.replace("shopping");
		resetPaymentSession();
		checkoutRetry$.next();
		playSoundCue("cancel");
	}

	override render() {
		// A genuinely not-found event carries no ticket types, basket, or
		// payment state to render underneath a dead header — <event-header>'s
		// own recovery state stands alone.
		if (eventDetailState.value.status === "not-found") {
			return html`
				<schmancy-surface ${fullHeight()} type="containerLowest" opacity="solid" class="relative w-full select-none touch-manipulation">
					<div ${overflowWithin()} class="h-full">
						<div class="mx-auto flex max-w-2xl flex-col gap-3">
							<event-header></event-header>
						</div>
					</div>
				</schmancy-surface>
			`;
		}

		const contact = contactState.value;
		const valid = contactValid.get();
		const phase = this.phase();
		const total = orderTotal.get();

		// "paid" is money settled, not tickets ready: the order's fulfillment
		// fan-out (tickets, PDF, receipt, .ics, wallet passes) runs after it,
		// and the push to /order lands only once that fan-out writes the
		// order's own access token at the end. The whole span reads as one
		// wait, because the buyer is waiting on one thing throughout.
		const sectionPhase = phase === "paid" ? "confirming" : phase;

		// The checkout owns the submitted payload until its terminal answer.
		const checkoutPending = sectionPhase === "confirming";
		// A card submit and a free order show this wait on the button pressed.
		const processingOverlay =
			checkoutPending && total > 0 && !this.cardSettlement;
		// Global stock can run out because this very checkout bought the last
		// ticket. Its own settlement or refusal owns this mounted payment surface.
		const soldOut =
			nothingToBuy.get() && !checkoutPending && sectionPhase !== "refused";

		// The contact fields and the payment section belong to a basket that
		// has something in it — an empty one has nothing to name a buyer for
		// and nothing to pay. Past the shopping beat they stay mounted
		// whatever the basket reads: tearing #payment-section out mid-flight
		// would destroy a live card iframe, and the refusal frame renders
		// inside that same section. A studio override drives those beats with
		// no basket at all, so it holds the stack open too.
		const checkoutVisible =
			basketFilled.get() ||
			sectionPhase !== "ready" ||
			this.phaseOverride !== null;
		// Paused sales name no buyer and take no payment, so the contact
		// fields and the Pay jump bar go; the payment section stays, on load and
		// on a sold-out event alike, because it carries the pause.
		const paused = salesPaused.value;

		return html`
			<schmancy-surface ${fullHeight()} type="containerLowest" opacity="solid" class="relative w-full select-none touch-manipulation">
				<!-- A sibling of the scroll container, so it holds the viewport
				     wherever the buyer has scrolled. It reports the wait and blocks
				     nothing. aria-hidden: the pay area's live region already narrates
				     this beat. -->
				${when(
					processingOverlay,
					() => html`
						<schmancy-busy
							class="pointer-events-none z-30 transition-opacity duration-300 ease-out starting:opacity-0 motion-reduce:transition-none"
							aria-hidden="true"
						>
							<schmancy-surface type="container" opacity="translucent" class="flex flex-col items-center gap-3 rounded-2xl p-4 text-center">
								<schmancy-spinner glass size="lg" color="primary"></schmancy-spinner>
								<schmancy-typography type="title" token="sm">
									${this.t("storefront.eventPage.processing.title")}
								</schmancy-typography>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t("storefront.eventPage.processing.body")}
								</schmancy-typography>
							</schmancy-surface>
						</schmancy-busy>
					`,
				)}

				<div ${overflowWithin()} class="h-full">
					<div ${ref(this.checkoutRootRef)} class="mx-auto flex max-w-2xl flex-col gap-3">
						<div ?inert=${checkoutPending} class="flex flex-col gap-3">
							<event-header .ticketsHere=${existingTicketToken.value !== null}>
								${when(
									refundEntryEventId.get(),
									(eventId) => html`
										<schmancy-button
											slot="actions"
											class="fixed bottom-4 right-4 z-40 shadow-lg"
											variant="outlined"
											color="neutral"
											size="sm"
											@click=${() =>
												area.push({
													area: "root",
													component: "refund",
													path: `${organizationPathPrefix.get()}/refund`,
													params: { e: eventId },
													historyStrategy: "push",
												})}
										>
											${this.t("storefront.eventPage.refundButton")}
										</schmancy-button>
									`,
								)}
							</event-header>
							${when(
								checkoutTestMode.get(),
								() =>
									html`<schmancy-typography type="label" token="sm" class="px-4 text-warning-default sm:px-6">${this.t("storefront.eventPage.payArea.testModeBanner")}</schmancy-typography>`,
							)}
							<!-- The tickets tied to no single day, above the chips: they
							     read the same under every one of them, so they stand once
							     here rather than repeating inside each day's list. Its own
							     px-4, since it sits outside the column below to keep the
							     full-bleed chip row between the two. The box is gated on
							     the same set the section renders, so an event with no
							     programme days pays no flex gap for a section it does not
							     have — the empty <div> would be a flex item all the same. -->
							${when(
								wholeEventRows.get().length > 0,
								() => html`
									<div class="px-4 sm:px-6">
										<type-selector section="wholeEvent"></type-selector>
									</div>
								`,
							)}
							<!-- Outside the px-4 below, deliberately: the chip row is
							     full-bleed and holds its own inline padding inside its own
							     scroller. The sticky bar is this page's, not the row's: it
							     resolves against this column's own scrollport, and
							     bg-surface-lowest matches the page so rows scrolling under
							     it disappear rather than collide with the chips. Gated on
							     the days themselves, so an event whose types declare no
							     entry window — which is most of them — pays not even this
							     column's gap for a bar it has no chips to hold. -->
							${when(
								programmeDays.get().length > 0,
								() => html`
									<div class="sticky top-0 z-10 bg-surface-lowest py-2">
										<programme-day-selector
											inline-padding="px-4 sm:px-6"
											.days=${programmeDays.get()}
											.value=${selectedProgrammeDay.get()}
											.inOrder=${basketProgrammeDays.get()}
											@change=${(e: CustomEvent<{ value: string }>) =>
												selectedProgrammeDayState.replace(e.detail.value)}
										></programme-day-selector>
									</div>
								`,
							)}
							<div class="flex flex-col gap-3 px-4 sm:px-6">
								<type-selector ${ref(this.typeSelectorRef)}></type-selector>

								${when(
									!paused && !soldOut && checkoutVisible,
									() => html`
										<div data-contact-fields ${ref(this.contactFieldsRef)} class="grid grid-cols-1 sm:grid-cols-2 gap-2">
											<schmancy-input
												class="select-text"
												placeholder=${this.t("storefront.eventPage.nameLabel")}
												type="text"
												autocomplete="name"
												required
												.value=${contact.name}
												.errorMessages=${{ valueMissing: this.t("storefront.eventPage.nameRequired") }}
												@input=${(e: CustomEvent<{ value: string }>) => setContact({ name: e.detail.value })}
											></schmancy-input>
											<schmancy-input
												class="select-text"
												placeholder=${this.t("storefront.eventPage.emailLabel")}
												type="email"
												autocomplete="email"
												required
												.value=${contact.email}
												.errorMessages=${{
													valueMissing: this.t(
														"storefront.eventPage.emailRequired",
													),
													typeMismatch: this.t(
														"storefront.eventPage.emailInvalid",
													),
												}}
												@input=${(e: CustomEvent<{ value: string }>) => setContact({ email: e.detail.value })}
											></schmancy-input>
											<!-- Mounted and collapsed under the ceiling, so crossing it
											     grows the field in place instead of inserting a node:
											     nothing above it moves — least of all the ticket steppers
											     the buyer is looking at while adding the ticket that
											     crosses. The reason sits above the field rather than in
											     the field's own hint slot, which the valueMissing message
											     takes over: a buyer who left it empty would lose the WHY
											     exactly when they need both. -->
											<div
												class="col-span-1 sm:col-span-2 flex flex-col gap-1"
												${reveal(addressRequired.get(), { preset: "gentle" })}
											>
												<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
													${this.t("storefront.eventPage.addressReason", {
														ceiling: ceilingFormatter.format(
															Order.KLEINBETRAG_CEILING_CENTS / 100,
														),
													})}
												</schmancy-typography>
												<schmancy-textarea
													class="select-text"
													placeholder=${this.t("storefront.eventPage.addressLabel")}
													rows="3"
													resize="none"
													required
													.value=${contact.address ?? ""}
													.errorMessages=${{ valueMissing: this.t("storefront.eventPage.addressRequired") }}
													@change=${(e: CustomEvent<{ value: string }>) => setContact({ address: e.detail.value })}
												></schmancy-textarea>
											</div>
										</div>
									`,
								)}
							</div>
						</div>

						${when(
							paused || (!soldOut && checkoutVisible),
							() => html`
								<!-- Last block in the scroll column, so its own bottom padding is the
								     page's only bottom breathing room. On a phone that edge lands under
								     the browser chrome and the home indicator, which is why the mobile
								     value is the larger one and carries the inset. -->
								<div
									id="payment-section"
									class="flex flex-col gap-2 px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-6"
								>
									${sectionPhase === "refused" ? this.refusal() : this.payArea(sectionPhase, valid, total, contact)}
								</div>
							`,
						)}

						${when(!paused && !soldOut && sectionPhase !== "refused" && total > 0 && !this.paymentInView, () => this.jumpBar(total))}
					</div>
				</div>
			</schmancy-surface>
		`;
	}

	/** The sticky handoff: count + total + one scroll CTA — never the
	 *  payment stack itself, so the bar stays slim and the page short. */
	private jumpBar(total: number) {
		const qty = totalBasketQty.get();

		return html`
			<schmancy-surface
				opacity="translucent"
				elevated
				class="sticky bottom-0 flex items-center justify-between gap-4 rounded-t-2xl px-4 py-3 sm:px-6"
				?inert=${checkoutJourney.value !== "shopping"}
			>
				<schmancy-typography type="title" token="sm" class="tabular-nums">
					${this.t(
						qty === 1
							? "storefront.eventPage.jumpBar.ticketOne"
							: "storefront.eventPage.jumpBar.ticketOther",
						{
							qty,
							total: formatCurrency(total / 100, "EUR", this.locale),
						},
					)}
				</schmancy-typography>
				<schmancy-button
					variant="filled"
					@click=${() => this.observedPaymentSection?.scrollIntoView({ behavior: "smooth", block: "start" })}
				>
					${this.t("storefront.eventPage.jumpBar.payCta")}
				</schmancy-button>
			</schmancy-surface>
		`;
	}

	private payArea(
		phase: "preparing" | "ready" | "confirming",
		valid: boolean,
		total: number,
		contact: ContactDraft,
	) {
		const session = paymentSession.value;

		if (salesPaused.value) return this.salesPausedNotice();

		// The organization's terms could not be loaded, so nothing here may
		// conclude an order on terms the buyer never saw: no card, wallet or
		// free-order control renders, and no clause claims their acceptance.
		// Asking again clears `failed` synchronously, which is also what keeps
		// a second click in the same tick from asking twice.
		if (legalContentState.value.failed)
			return html`
				<schmancy-surface type="error" role="alert" class="flex flex-col gap-2 rounded-lg p-3">
					<schmancy-typography type="body" token="sm">${this.t("legal.unavailable")}</schmancy-typography>
					<schmancy-button size="xs" variant="outlined" @click=${() => {
						if (legalContentState.value.failed) legalRetry$.next();
					}}>
						${this.t("storefront.eventPage.statusLine.retry")}
					</schmancy-button>
				</schmancy-surface>
			`;
		// Nor before they have answered at all: until then the default is all
		// there is to show, and an order concluded on it would bind the buyer
		// to terms that may not be the organization's.
		if (!legalContentState.value.settled)
			return html`
				<div class="flex flex-col gap-3" aria-hidden="true">
					<schmancy-skeleton class="h-12 w-full rounded-xl"></schmancy-skeleton>
				</div>
				${this.statusLine("preparing")}
			`;

		// A zero-total basket carries no payment leg: one commit control, no
		// card fields, no wallets — so its own settling window is the
		// debounced items-edit write alone, never amountInSync (which
		// requires a "ready" payment session a free order never gets).
		if (total === 0) {
			// One predicate for the enabled state and for the write the mounted
			// `.sync` makes — checkout.state.ts's freeConfirmable, which folds in
			// the contact the SERVER has (settle refuses a null buyer, and the
			// draft reaches the document on the edit-write's own debounce), the
			// write rule's own line bound, and the in-flight edit-write.
			const confirmable = freeConfirmable.get();
			const freeLabel = amountSyncing.value
				? this.t("storefront.eventPage.payArea.payLabelSettling")
				: this.t("storefront.eventPage.payArea.freeOrder");
			return html`
				<div class="relative">
					<div class="flex flex-col gap-2">
						<schmancy-button variant="filled" width="full" ?disabled=${!confirmable && phase !== "confirming"} .busy=${phase === "confirming"} @click=${() => {
							if (checkoutJourney.value !== "confirming") this.confirmPayment();
						}}>
							${freeLabel}
						</schmancy-button>
					</div>
				</div>
				<checkout-disclosures .lines=${orderLines.get()}></checkout-disclosures>
				${this.statusLine(phase)}
			`;
		}

		// Read once — payLabel, submitDisabled, and paymentStackBlock below
		// all gate on the SAME in-sync read within this one render pass.
		const inSync = amountInSync.get();

		// The whisper: the button quietly says "Updating…" while the widget's
		// amount is untrustworthy (a live session whose amount hasn't caught
		// up to the basket yet), then relabels to the real total — no
		// spinner, no caption, no layout shift.
		const payLabel = inSync
			? this.t("storefront.eventPage.payArea.payLabel", {
					total: formatCurrency(total / 100, "EUR", this.locale),
				})
			: this.t("storefront.eventPage.payArea.payLabelSettling");

		// The same whisper in its retry tense — the widget swaps the CTA to this
		// label the moment it raises any decline, and it cannot inject the
		// amount into a host string, so the total is composed here exactly as it
		// is for payLabel. Settling wins over the retry tense for the same reason
		// it wins over the pay tense: a stale total on a live button is the one
		// thing the whisper exists to prevent.
		const retryPayLabel = inSync
			? this.t("storefront.eventPage.notice.retryPay", {
					total: formatCurrency(total / 100, "EUR", this.locale),
				})
			: this.t("storefront.eventPage.payArea.payLabelSettling");

		// The words the widget raises above its own pay button when a payment
		// fails — the host's copy, keyed by what the buyer can do, so the vendor's
		// English string never reaches a screen. Guarded on the UI language rather
		// than rebuilt inline: a fresh object identity every render would set the
		// property, and so re-render the widget, on every keystroke in the contact
		// fields, for copy that only changes when the buyer switches language.
		// `satisfies` is the only thing that checks the keys: guard() erases the
		// object's type at the binding, so a member the widget's union gains would
		// otherwise go missing here silently and fall back to vendor English.
		const noticeCopy = guard(
			[this.uiLanguage],
			() =>
				({
					insufficientFunds: {
						headline: this.t(
							"storefront.eventPage.notice.insufficientFunds.headline",
						),
						body: this.t("storefront.eventPage.notice.insufficientFunds.body"),
					},
					expiredCard: {
						headline: this.t(
							"storefront.eventPage.notice.expiredCard.headline",
						),
						body: this.t("storefront.eventPage.notice.expiredCard.body"),
					},
					cardDetails: {
						headline: this.t(
							"storefront.eventPage.notice.cardDetails.headline",
						),
						body: this.t("storefront.eventPage.notice.cardDetails.body"),
					},
					contactDetails: {
						headline: this.t(
							"storefront.eventPage.notice.contactDetails.headline",
						),
						body: this.t("storefront.eventPage.notice.contactDetails.body"),
					},
					authentication: {
						headline: this.t(
							"storefront.eventPage.notice.authentication.headline",
						),
						body: this.t("storefront.eventPage.notice.authentication.body"),
					},
					bankDeclined: {
						headline: this.t(
							"storefront.eventPage.notice.bankDeclined.headline",
						),
						body: this.t("storefront.eventPage.notice.bankDeclined.body"),
					},
					cardLocked: {
						headline: this.t("storefront.eventPage.notice.cardLocked.headline"),
						body: this.t("storefront.eventPage.notice.cardLocked.body"),
					},
					limitReached: {
						headline: this.t(
							"storefront.eventPage.notice.limitReached.headline",
						),
						body: this.t("storefront.eventPage.notice.limitReached.body"),
					},
					cardNotActivated: {
						headline: this.t(
							"storefront.eventPage.notice.cardNotActivated.headline",
						),
						body: this.t("storefront.eventPage.notice.cardNotActivated.body"),
					},
					cardNotSupported: {
						headline: this.t(
							"storefront.eventPage.notice.cardNotSupported.headline",
						),
						body: this.t("storefront.eventPage.notice.cardNotSupported.body"),
					},
					technical: {
						headline: this.t("storefront.eventPage.notice.technical.headline"),
						body: this.t("storefront.eventPage.notice.technical.body"),
					},
					refused: {
						headline: this.t("storefront.eventPage.notice.refused.headline"),
						body: this.t("storefront.eventPage.notice.refused.body"),
					},
					orderStale: {
						headline: this.t("storefront.eventPage.notice.orderStale.headline"),
						body: this.t("storefront.eventPage.notice.orderStale.body"),
					},
					blocked: {
						headline: this.t("storefront.eventPage.notice.blocked.headline"),
						body: this.t("storefront.eventPage.notice.blocked.body"),
						remedy: this.t("storefront.eventPage.notice.blocked.remedy"),
					},
				}) satisfies SchmancyRevoluteNoticeCopy,
		);

		// Priority chain — the unavailable sub-state replaces the skeleton
		// outright, it never renders underneath it. "missingKey" joins
		// "error" here because to a buyer the two are one fact with one
		// remedy: nothing was charged, come back. Which of them it is
		// answers to a developer, and revolut.api.ts (the studio's dev
		// proxy, the only writer of missingKey) says so on the console.
		const countryNames = new Intl.DisplayNames([this.locale], {
			type: "region",
		});
		const billingAddress =
			BILLING_COUNTRY_CODES.includes(this.billingCountry) &&
			this.billingPostcode.trim()
				? ({
						countryCode: this.billingCountry,
						postcode: this.billingPostcode.trim(),
					} satisfies NonNullable<SchmancyRevolute["billingAddress"]>)
				: undefined;
		const methodStack =
			session.status === "error" ||
			session.status === "missingKey" ||
			this.retryingPayment
				? html`
					<div ${reveal(true, { preset: "snappy" })}>
						<schmancy-surface type="error" role="alert" class="flex flex-col gap-2 rounded-lg p-3">
							<schmancy-typography type="body" token="sm">
								${this.t("storefront.eventPage.statusLine.prepareUnavailable")}
							</schmancy-typography>
							<schmancy-button size="xs" variant="outlined" .busy=${this.retryingPayment} @click=${() => {
								if (this.retryingPayment) return;
								this.retryingPayment = true;
								checkoutRetry$.next();
							}}>
								${this.t("storefront.eventPage.statusLine.retry")}
							</schmancy-button>
						</schmancy-surface>
					</div>
				`
				: phase === "preparing"
					? html`
							<!-- Wallet and closed card controls each reserve 48px,
							     with the payment stack's same 12px gap between them. -->
							<div class="flex flex-col gap-3" aria-hidden="true">
								${when(
									!!paymentPublicKey.get(),
									() => html`
										<schmancy-skeleton class="h-12 w-full rounded-xl"></schmancy-skeleton>
									`,
								)}
								<schmancy-skeleton class="h-12 w-full rounded-xl"></schmancy-skeleton>
							</div>
						`
					: session.status === "ready"
						? html`
								<!-- .payerName rides the buyer's own typing through exactly as
								     typed: a blank name stays blank, because the widget omits an
								     absent name from its submit meta, and a name invented here
								     is a claim the issuer can reject as a mismatch. Plain, not
								     guard()ed like noticeCopy: a string re-binds only when its
								     value actually changes, so a keystroke in a sibling field
								     costs nothing while the name the submit reads stays current. -->
								<!-- .sandboxMode is the money gate, deliberately the opposite
								     polarity to the test-mode line further down: an unknown
								     verdict is CAUTIOUS in behaviour (mount sandbox) and SILENT
								     in disclosure (say nothing), because a wrong disclosure is a
								     false statement to a paying buyer while a cautious mount is
								     merely conservative. session.env is the server-minted
								     checkout session's own env and stays authoritative wherever
								     it is set. -->
								<schmancy-revolute
									${ref(this.paymentWidgetRef)}
									${gravity({ distance: 12, mass: 1.4 })}
									.sandboxMode=${session.env != null ? session.env !== "live" : paymentMode.get() !== "live"}
									.amount=${session.syncedAmount}
									.currency=${"EUR"}
									.merchantPublicKey=${paymentPublicKey.get() ?? ""}
									.email=${contact.email}
									.payerName=${contact.name}
									.billingAddress=${billingAddress}
									.cardDisclosureLabel=${this.t("storefront.eventPage.cardDisclosureLabel")}
									.enableRevolutPay=${false}
									.payLabel=${payLabel}
									.retryPayLabel=${retryPayLabel}
									.noticeCopy=${noticeCopy}
									.submitDisabled=${!valid || !billingAddress || (!inSync && phase !== "confirming")}
									.settling=${phase === "confirming" && this.cardSettlement}
									@schmancy-revolute-tracking=${(
										e: CustomEvent<SchmancyRevoluteTrackingDetail>,
									) => {
										if (e.detail.step === "PAYMENT_SUCCESS")
											this.cardSettlement = e.detail.method === "card";
									}}
									@schmancy-revolute-request-order=${(
										e: CustomEvent<{
											resolve: (o: { token: string }) => void;
											reject: (err: Error) => void;
										}>,
									) => this.requestOrder(e.detail)}
									@schmancy-revolute-processing=${(
										e: CustomEvent<{ processing: boolean }>,
									) => {
										// A card submit starting — the only path that raises
										// this. Locks the basket earlier than the success
										// callback, so a mid-flight tap can never touch a
										// basket the payment is already committing against.
										// A wallet locks it on its own success, or on the
										// mounted `.sync` reading an authorising document.
										if (e.detail.processing) {
											this.widgetSucceeded = false;
											this.widgetProcessing = true;
											this.cardSettlement = true;
											checkoutJourney.replace("confirming");
											return;
										}
										if (!this.widgetProcessing) return;
										this.widgetProcessing = false;
										this.cardSettlement = false;
										// The release: a dismissed 3DS challenge (or any
										// other conclusion the widget reaches with no
										// success) hands the page back to a live checkout
										// with its originating action available again.
										if (
											releasesOnProcessingStop(
												checkoutJourney.value,
												this.widgetSucceeded,
											)
										)
											checkoutJourney.replace("shopping");
									}}
									@schmancy-revolute-success=${() => {
										this.widgetSucceeded = true;
										this.confirmPayment();
									}}
									@schmancy-revolute-error=${(
										e: CustomEvent<SchmancyRevoluteErrorDetail>,
									) => {
										// Telemetry only — the buyer's words come from
										// .noticeCopy, keyed by what they can do next, and the
										// vendor's opaque English rides this event for the log
										// alone. The class rides beside it because two
										// different failures reach the log as one identical
										// vendor sentence. No class at all is the widget's
										// silent kind — a stale refresh it auto-retries and
										// raises no notice for — so it warns rather than
										// errors and leaves the journey where it is: releasing
										// "confirming" would drop the buyer out of a payment
										// nothing has told them failed.
										if (!e.detail.failure) {
											console.warn(
												"Revolut payment notice (silent):",
												e.detail.message,
											);
											return;
										}
										console.error(
											"Revolut payment error:",
											e.detail.failure,
											e.detail.message,
										);
										// The order id, not the checkout id: the mounted `.sync`
										// owns which document may be retired, but only the
										// widget knows which PSP order it was actually mounted
										// from — and the two name different sessions the moment
										// a second tab moves the shared device pointer.
										paymentSessionSpent$.next(session.orderId);
										this.cardSettlement = false;
										if (checkoutJourney.value === "confirming")
											checkoutJourney.replace("shopping");
									}}
								>
									<span slot="card-summary" class="flex shrink-0 items-center gap-2">
										<img class="h-8 w-8" alt=${
											// catalog-text-ok: official payment-network brand name
											"Visa"
										} width="32" height="32" src="https://assets.revolut.com/assets/icons/LogoVisaColour.svg" />
										<img class="h-8 w-8" alt=${
											// catalog-text-ok: official payment-network brand name
											"Mastercard"
										} width="32" height="32" src="https://assets.revolut.com/assets/icons/LogoMc.svg" />
									</span>
									<div slot="card-billing" data-card-billing class="grid grid-cols-1 sm:grid-cols-2 gap-2">
										<schmancy-autocomplete
											placeholder=${this.t("storefront.eventPage.billingCountryLabel")}
											aria-label=${this.t("storefront.eventPage.billingCountryLabel")}
											autocomplete="billing country"
											required
											.disabled=${phase === "confirming"}
											.value=${this.billingCountry}
											.errorMessages=${{ valueMissing: this.t("storefront.eventPage.billingCountryRequired") }}
											@change=${(
												e: CustomEvent<{ value: string | string[] }>,
											) => {
												if (
													this.phase() === "confirming" ||
													this.phase() === "paid"
												)
													return;
												this.billingCountry =
													typeof e.detail.value === "string" &&
													BILLING_COUNTRY_CODES.includes(e.detail.value)
														? e.detail.value
														: "";
											}}
										>
											${repeat(
												BILLING_COUNTRY_CODES.map((code) => ({
													code,
													label: countryNames.of(code) ?? code,
												})).sort((a, b) =>
													a.label.localeCompare(b.label, this.locale),
												),
												(country) => country.code,
												(country) =>
													html`<schmancy-option value=${country.code} label=${country.label}></schmancy-option>`,
											)}
										</schmancy-autocomplete>
										<schmancy-input
											placeholder=${this.t("storefront.eventPage.billingPostcodeLabel")}
											aria-label=${this.t("storefront.eventPage.billingPostcodeLabel")}
											autocomplete="billing postal-code"
											required
											.disabled=${phase === "confirming"}
											.value=${this.billingPostcode}
											.errorMessages=${{ valueMissing: this.t("storefront.eventPage.billingPostcodeRequired") }}
											@input=${(e: CustomEvent<{ value: string }>) => {
												if (
													this.phase() === "confirming" ||
													this.phase() === "paid"
												)
													return;
												this.billingPostcode = e.detail.value;
											}}
										></schmancy-input>
									</div>
								</schmancy-revolute>
							`
						: html`
								<div class="flex flex-col gap-2">
									<div ${reveal(true, { preset: "snappy" })}>
										<schmancy-surface type="warning" role="status" aria-live="polite" class="flex flex-col gap-2 rounded-lg p-3">
											<schmancy-typography type="body" token="sm">
												${this.t("storefront.eventPage.payArea.unavailable")}
											</schmancy-typography>
										</schmancy-surface>
									</div>
									<schmancy-button variant="filled" width="full" ?disabled=${!valid || phase === "confirming"}>
										${payLabel}
									</schmancy-button>
								</div>
							`;

		// checkout.state.ts's paymentStackBlock/paymentStackInert are the shared
		// rules this view and their own tests reason over — see their own doc
		// comments there for the mute/blocked/inert semantics.
		const { mute, blocked, missingContact } = paymentStackBlock(
			phase,
			session,
			inSync,
		);
		const inert = phase !== "confirming" && paymentStackInert(session, inSync);

		return html`
			<!-- § 312j Abs. 2 BGB: the total-price-incl.-VAT statement, in
			     hervorgehobener Weise immediately above the whole payment
			     block — the wallet express-pay buttons methodStack renders
			     included, since a tap there concludes the order in one
			     gesture just as a card submit does. Gated the same way as
			     the trust footnote below: nothing to disclose while the
			     basket is between prices (a re-price, a retried land).

			     The statement is align="right", so its whole left half is
			     empty — and that empty half is the code's ENTIRE footprint on
			     this page. No row of its own, no gap around one, no line item:
			     the band was already here and the statement stays flush right.
			     Measured at 390px, the band stands 24px with the trigger in it
			     against the statement's own 20px, and the whole payment
			     section 340px against 336px — four pixels, once, at first
			     paint, and none on any transition. -->
			${when(
				orderLines.get().length > 0,
				() => html`
					<div class="flex items-center justify-between gap-3">
						<voucher-code
							@show-tickets=${() =>
								this.typeSelectorRef.value?.scrollIntoView({
									behavior: "smooth",
									block: "start",
								})}
						></voucher-code>
						<checkout-disclosures
							priceOnly
							.lines=${orderLines.get()}
							.discountCents=${appliedVoucher.get()?.discountCents ?? 0}
						></checkout-disclosures>
					</div>
				`,
			)}
			<!-- An EXPLICIT sandbox and nothing else, read like the widget's env:
			     the session's own env first, the card's mode otherwise. Neither
			     answered is unknown, and a live deployment must never tell a
			     buyer it is in test mode on a slow or failed load; the widget
			     above takes the opposite polarity on the same unknown. -->
			${when(
				checkoutTestMode.get(),
				() => html`
					<schmancy-typography type="label" token="md" class="text-warning-default">
						${this.t("storefront.eventPage.payArea.testModeBanner")}
					</schmancy-typography>
				`,
			)}
			<div class="relative">
				<div ${curtain({ active: mute })} ?inert=${inert} class="flex flex-col gap-2">${methodStack}</div>
				<div
					class=${this.classMap({
						"absolute inset-0 z-10 flex items-center justify-center transition duration-500 ease-out motion-reduce:transition-none": true,
						"opacity-100 translate-y-0": mute,
						"opacity-0 translate-y-2": !mute,
						"pointer-events-auto": blocked,
						"pointer-events-none": !blocked,
					})}
					aria-hidden="true"
					@click=${() => {
						if (!missingContact) return;
						this.contactFieldsRef.value?.scrollIntoView({
							behavior: "smooth",
							block: "center",
						});
						playSoundCue("overdue-alert");
						// Names the field that is actually missing — a buyer who
						// already typed name and email and is only short the
						// above-ceiling address would otherwise be pointed at two
						// fields they can see are filled.
						$notify.info(
							this.t(
								addressRequired.get() &&
									(contactState.value.address ?? "").trim().length === 0
									? "storefront.eventPage.payArea.addressNeededToast"
									: "storefront.eventPage.payArea.contactNeededToast",
							),
							{ playSound: false },
						);
					}}
				></div>
			</div>
			<!-- Announced, never painted: the sighted answer to confirming is the
			     pressed button's busy state or the processing overlay. -->
			${this.statusLine(phase)}
			<!-- The § 305/Art. 13/§ 5 DDG footnote — terms, privacy, imprint —
			     sits under the CTA, where the § 312j price notice sits above it.
			     Gated on there being something to disclose, not on the payment
			     session: this footer reads its lines property and nothing else, and the
			     session legitimately returns to "creating" mid-page (a re-price, a
			     retried land). Keying it to the session would blink it out for that
			     whole window — 64px of layout jumping under the buyer's cursor, and
			     the Art. 13 privacy line vanishing from under the CTA exactly while
			     they are deciding to pay. The skeleton standing in for the widget
			     is still a CTA, and this belongs under it. -->
			${when(
				orderLines.get().length > 0,
				() => html`
					<checkout-disclosures
						.lines=${orderLines.get()}
						.discountCents=${appliedVoucher.get()?.discountCents ?? 0}
					></checkout-disclosures>
				`,
			)}
		`;
	}

	/** The sr-only narration for every transient beat (preparing, syncing,
	 *  confirming), and the page's one role="status" while it lasts.
	 *  Session-error and missingKey render in payArea's methodStack, replacing
	 *  the skeleton outright rather than narrating underneath it; a payment
	 *  failure renders as the widget's own alert above the pay button, from the
	 *  copy this page hands it. */
	private statusLine(phase: "preparing" | "ready" | "confirming") {
		const session = paymentSession.value;
		const syncing = amountSyncing.value;

		const narration =
			phase === "confirming"
				? this.t("storefront.eventPage.statusLine.confirming")
				: phase === "preparing" &&
						session.status !== "missingKey" &&
						session.status !== "error"
					? this.t("storefront.eventPage.statusLine.preparing")
					: syncing
						? this.t("storefront.eventPage.statusLine.syncing")
						: "";

		return html`
			<schmancy-visually-hidden role="status" aria-live="polite">${narration}</schmancy-visually-hidden>
		`;
	}

	/** Nothing the buyer can do makes a sale go through, so the notice carries
	 *  no control. */
	private salesPausedNotice() {
		return html`
			<schmancy-surface type="warning" role="status" class="flex flex-col gap-2 rounded-lg p-3">
				<schmancy-typography type="body" token="sm">
					${this.t("storefront.eventPage.payArea.salesPaused")}
				</schmancy-typography>
			</schmancy-surface>
		`;
	}

	private refusal() {
		const session = paymentSession.value;
		const refusalReason =
			session.status === "error" && session.reason === "refused"
				? session.refusalReason
				: "internal";
		// A restart would be refused again: the licence pauses the whole shop.
		if (refusalReason === "licenceReadOnly") return this.salesPausedNotice();
		if (refusalReason === "internal")
			return html`<schmancy-typography type="body" token="sm" role="status">${this.t("storefront.eventPage.statusLine.prepareUnavailable")}</schmancy-typography>`;

		const body =
			refusalReason === "soldOut"
				? this.t("storefront.eventPage.refusal.body")
				: refusalReason === "notEligible"
					? this.t("storefront.eventPage.refusal.bodyBasketChanged")
					: this.t("storefront.eventPage.refusal.bodyGeneric");
		const restartLabel =
			refusalReason === "notEligible"
				? this.t("storefront.eventPage.refusal.continueWithTotal")
				: this.t("storefront.eventPage.refusal.restart");

		return html`
			<div class="flex flex-col gap-3">
				<schmancy-typography type="title" token="sm">${this.t("storefront.eventPage.refusal.title")}</schmancy-typography>
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${body}</schmancy-typography>
				<schmancy-button variant="filled" width="full" @click=${() => this.restart()}>${restartLabel}</schmancy-button>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-page": EventPageView;
	}
}
